import { useState, useEffect, useRef } from 'react';
import dompurify from 'dompurify';
import { useApolloClient } from '@apollo/client';
import {
	Button,
	Card,
	Row,
	Col,
	Empty,
	Spin,
	Modal,
	Form,
	Input,
	Switch,
	Image,
	Popconfirm,
	Tooltip,
	message,
} from 'antd';
import {
	PlusOutlined,
	EditOutlined,
	DeleteOutlined,
	AppstoreOutlined,
	UnorderedListOutlined,
} from '@ant-design/icons';
import { motion, AnimatePresence } from 'motion/react';
import {
	QUERY_REWARDS_STORE_REWARDS,
	CREATE_REWARDS_STORE_REWARD,
	UPDATE_REWARDS_STORE_REWARD,
	DELETE_REWARDS_STORE_REWARD,
} from 'src/rewardsStore/RewardsStoreGraphQL';
import gql from 'graphql-tag';

const UPDATE_BADGE_MILESTONE_REWARD = gql`
	mutation UpdateBadge($input: UpdateBadgeInput!) {
		updateBadge(input: $input) {
			id
			milestoneRewardId
		}
	}
`;
import AddMilestoneRewardModal from './AddMilestoneRewardModal.jsx';

const OverflowTooltip = ({ title, children }) => {
	const ref = useRef(null);
	const [isOverflowing, setIsOverflowing] = useState(false);

	useEffect(() => {
		const el = ref.current;
		if (el) {
			setIsOverflowing(el.scrollWidth > el.clientWidth);
		}
	}, []);

	return (
		<Tooltip title={isOverflowing ? title : null} getPopupContainer={(trigger) => trigger.parentElement}>
			<div ref={ref} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
				{children}
			</div>
		</Tooltip>
	);
};

const MilestoneRewardCard = ({ currentUser, onRewardsLoaded, milestoneBadges, refreshBadges }) => {
	const client = useApolloClient();
	const companyId = currentUser?.company?.id;

	const [rewards, setRewards] = useState([]);
	const [loading, setLoading] = useState(true);
	const [addModalOpen, setAddModalOpen] = useState(false);
	const [addLoading, setAddLoading] = useState(false);
	const [editModalOpen, setEditModalOpen] = useState(false);
	const [editingReward, setEditingReward] = useState(null);
	const [viewMode, setViewMode] = useState('grid');
	const [editForm] = Form.useForm();

	useEffect(() => {
		if (companyId) {
			fetchRewards();
		}
	}, [companyId]);

	const fetchRewards = async () => {
		try {
			setLoading(true);
			const { data } = await client.query({
				query: QUERY_REWARDS_STORE_REWARDS,
				variables: {
					companyId,
					rewardType: 'milestone',
					limit: 100,
				},
				fetchPolicy: 'network-only',
			});

			if (data?.queryRewardsStoreRewardsByCompanyIdRewardType?.items) {
				const items = [...data.queryRewardsStoreRewardsByCompanyIdRewardType.items];
				items.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
				setRewards(items);
				if (onRewardsLoaded) onRewardsLoaded(items);
			}
		} catch (error) {
			console.error('Error fetching milestone rewards:', error);
			message.error('Failed to load milestone rewards');
		} finally {
			setLoading(false);
		}
	};

	const handleAddReward = async (rewardInput) => {
		try {
			setAddLoading(true);
			const { data } = await client.mutate({
				mutation: CREATE_REWARDS_STORE_REWARD,
				variables: { input: rewardInput },
			});

			if (data?.createRewardsStoreReward) {
				await fetchRewards();
				setAddModalOpen(false);
				message.success('Milestone reward added!');
			}
		} catch (error) {
			console.error('Error adding milestone reward:', error);
			message.error('Failed to add milestone reward');
		} finally {
			setAddLoading(false);
		}
	};

	const handleEditReward = async (values) => {
		try {
			const { data } = await client.mutate({
				mutation: UPDATE_REWARDS_STORE_REWARD,
				variables: {
					input: {
						id: editingReward.id,
						companyId,
						name: values.name,
						description: values.description,
						image: values.image,
						active: values.active,
						amount: 0,
						rewardType: 'milestone',
						externalId: editingReward.externalId || 'no_external',
					},
				},
			});

			if (data?.updateRewardsStoreReward) {
				await fetchRewards();
				setEditModalOpen(false);
				setEditingReward(null);
				editForm.resetFields();
				message.success('Milestone reward updated!');
			}
		} catch (error) {
			console.error('Error updating milestone reward:', error);
			message.error('Failed to update milestone reward');
		}
	};

	const handleDeleteReward = async (rewardId, affectedBadges = []) => {
		try {
			const { data } = await client.mutate({
				mutation: DELETE_REWARDS_STORE_REWARD,
				variables: { companyId, id: rewardId },
			});

			if (data?.deleteRewardsStoreReward) {
				if (affectedBadges.length > 0) {
					await Promise.all(
						affectedBadges.map((badge) =>
							client.mutate({
								mutation: UPDATE_BADGE_MILESTONE_REWARD,
								variables: {
									input: {
										id: badge.id,
										companyId,
										updatedBy: currentUser?.id,
										milestoneRewardId: null,
									},
								},
							})
						)
					);
					if (refreshBadges) refreshBadges();
				}

				await fetchRewards();
				message.success('Milestone reward deleted!');
			}
		} catch (error) {
			console.error('Error deleting milestone reward:', error);
			message.error('Failed to delete milestone reward');
		}
	};

	const openEditModal = (reward) => {
		setEditingReward(reward);
		editForm.setFieldsValue({
			name: reward.name,
			description: reward.description,
			image: reward.image,
			active: reward.active,
		});
		setEditModalOpen(true);
	};

	return (
		<div
			style={{
				background: '#fff',
				borderRadius: '14px',
				padding: '24px',
				border: '1px solid #ddd',
				marginBottom: '24px',
			}}
		>
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					marginBottom: '20px',
				}}
			>
				<div>
					<h3
						style={{
							fontSize: '18px',
							fontWeight: '600',
							color: '#555',
							margin: 0,
						}}
					>
						Milestone Rewards (Snappy Gifts)
					</h3>
					<p style={{ color: '#595959', fontSize: '13px', margin: '4px 0 0' }}>
						Add rewards from Snappy that can be assigned to milestone celebrations.
					</p>
				</div>
				<div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
					<div style={{ display: 'inline-flex', borderRadius: 8, overflow: 'hidden', border: '2px solid #f5f5f5' }}>
						{[
							{ value: 'grid', icon: <AppstoreOutlined /> },
							{ value: 'compact', icon: <UnorderedListOutlined /> },
						].map((opt) => (
							<div
								key={opt.value}
								onClick={() => setViewMode(opt.value)}
								style={{
									padding: '6px 12px',
									cursor: 'pointer',
									background: viewMode === opt.value ? '#fff' : '#f5f5f5',
									color: viewMode === opt.value ? '#1a1a2e' : '#595959',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									fontSize: 20,
									transition: 'background 0.2s, color 0.2s',
								}}
							>
								{opt.icon}
							</div>
						))}
					</div>
					<Button
						type="primary"
						icon={<PlusOutlined />}
						onClick={() => setAddModalOpen(true)}
						style={{ borderRadius: 8 }}
					>
						Add Reward
					</Button>
				</div>
			</div>

			{loading ? (
				<div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
					<Spin size="large" />
				</div>
			) : rewards.length === 0 ? (
				<Empty
					description="No milestone rewards yet"
					style={{ padding: '40px 0' }}
				>
					<Button type="primary" onClick={() => setAddModalOpen(true)}>
						Add Your First Reward
					</Button>
				</Empty>
			) : viewMode === 'grid' ? (
				<AnimatePresence mode="popLayout">
					<Row gutter={[16, 16]}>
						{rewards.map((reward, index) => (
							<Col xs={24} sm={12} md={8} lg={6} key={reward.id}>
								<motion.div
									layout
									initial={{ opacity: 0, y: 20 }}
									animate={{ opacity: 1, y: 0 }}
									exit={{ opacity: 0, scale: 0.9 }}
									transition={{ duration: 0.3, delay: index * 0.05 }}
								>
									<Card
										hoverable
										style={{
											borderRadius: '14px',
											overflow: 'hidden',
											position: 'relative',
										}}
										cover={
											reward.image ? (
												<div
													style={{
														height: 180,
														overflow: 'hidden',
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'center',
														backgroundColor: '#fafafa',
													}}
												>
													<Image
														src={reward.image}
														alt={reward.name}
														style={{
															width: '100%',
															height: 180,
															objectFit: 'cover',
														}}
														preview={false}
													/>
												</div>
											) : (
												<div
													style={{
														height: 180,
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'center',
														backgroundColor: '#f5f5f5',
														color: '#ccc',
														fontSize: '48px',
													}}
												>
													🎁
												</div>
											)
										}
									>
										<div
											style={{
												position: 'absolute',
												top: 10,
												right: 10,
												width: 12,
												height: 12,
												borderRadius: '50%',
												backgroundColor: reward.active ? '#52c41a' : '#bfbfbf',
												border: '2px solid #fff',
												boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
											}}
										/>
										<Card.Meta
											title={
												<OverflowTooltip title={reward.name}>
													<span style={{ fontWeight: 600, fontSize: '14px' }}>
														{reward.name}
													</span>
												</OverflowTooltip>
											}
											description={
												<div
													style={{
														fontSize: '12px',
														height: '40px',
														overflow: 'hidden',
														display: '-webkit-box',
														WebkitLineClamp: 2,
														WebkitBoxOrient: 'vertical',
													}}
													dangerouslySetInnerHTML={{
														__html: dompurify.sanitize(
															reward.description
																? reward.description
																	.replace('<p><p>', '')
																	.replace('</p></p>', '')
																: '&nbsp;'
														),
													}}
												/>
											}
										/>
										<div style={{ display: 'flex', marginTop: 12 }}>
											<div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
												<Button
													type="link"
													icon={<EditOutlined />}
													onClick={() => openEditModal(reward)}
													size="small"
													style={{ padding: 0 }}
												>
													Edit
												</Button>
											</div>
											<div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
												{(() => {
													const affected = (milestoneBadges || []).filter((b) => b.milestoneRewardId === reward.id);
													return (
														<Popconfirm
															title={affected.length > 0
																? 'Deleting this reward will also remove it from the badge(s) it is attached to, are you sure?'
																: 'Delete this reward?'}
															onConfirm={() => handleDeleteReward(reward.id, affected)}
															okText="Yes"
															cancelText="No"
														>
															<Button
																type="link"
																danger
																icon={<DeleteOutlined />}
																size="small"
																style={{ padding: 0 }}
															>
																Delete
															</Button>
														</Popconfirm>
													);
												})()}
											</div>
										</div>
									</Card>
								</motion.div>
							</Col>
						))}
					</Row>
				</AnimatePresence>
			) : (
				<AnimatePresence mode="popLayout">
					<div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
						{rewards.map((reward, index) => (
							<motion.div
								key={reward.id}
								layout
								initial={{ opacity: 0, x: -20 }}
								animate={{ opacity: 1, x: 0 }}
								exit={{ opacity: 0, x: 20 }}
								transition={{ duration: 0.25, delay: index * 0.03 }}
								style={{
									display: 'flex',
									alignItems: 'center',
									gap: 16,
									padding: '10px 12px',
									borderRadius: 10,
									background: '#fafafa',
									border: '1px solid #f0f0f0',
								}}
							>
								{reward.image ? (
									<img
										src={reward.image}
										alt={reward.name}
										style={{
											width: 56,
											height: 56,
											borderRadius: 8,
											objectFit: 'cover',
											flexShrink: 0,
										}}
									/>
								) : (
									<div
										style={{
											width: 56,
											height: 56,
											borderRadius: 8,
											backgroundColor: '#f5f5f5',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											flexShrink: 0,
											color: '#ccc',
											fontSize: '24px',
										}}
									>
										🎁
									</div>
								)}
								<div style={{ flex: 1, minWidth: 0 }}>
									<div style={{ fontWeight: 600, fontSize: '14px', color: '#1a1a2e', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
										{reward.name}
									</div>
									{reward.description && (
										<div
											style={{
												fontSize: '12px',
												color: '#595959',
												marginTop: 2,
												overflow: 'hidden',
												display: '-webkit-box',
												WebkitLineClamp: 2,
												WebkitBoxOrient: 'vertical',
											}}
											dangerouslySetInnerHTML={{
												__html: dompurify.sanitize(
													reward.description
														.replace('<p><p>', '')
														.replace('</p></p>', '')
												),
											}}
										/>
									)}
								</div>
								<div
									style={{
										width: 10,
										height: 10,
										borderRadius: '50%',
										backgroundColor: reward.active ? '#52c41a' : '#bfbfbf',
										flexShrink: 0,
									}}
								/>
								<div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
									<Button
										type="text"
										icon={<EditOutlined style={{ color: '#1890ff' }} />}
										onClick={() => openEditModal(reward)}
										size="small"
									/>
									{(() => {
										const affected = (milestoneBadges || []).filter((b) => b.milestoneRewardId === reward.id);
										return (
											<Popconfirm
												title={affected.length > 0
													? 'Deleting this reward will also remove it from the badge(s) it is attached to, are you sure?'
													: 'Delete this reward?'}
												onConfirm={() => handleDeleteReward(reward.id, affected)}
												okText="Yes"
												cancelText="No"
											>
												<Button
													type="text"
													danger
													icon={<DeleteOutlined />}
													size="small"
												/>
											</Popconfirm>
										);
									})()}
								</div>
							</motion.div>
						))}
					</div>
				</AnimatePresence>
			)}

			<AddMilestoneRewardModal
				open={addModalOpen}
				onCancel={() => setAddModalOpen(false)}
				onSubmit={handleAddReward}
				currentUser={currentUser}
				client={client}
				loading={addLoading}
			/>

			<Modal
				title="Edit Milestone Reward"
				open={editModalOpen}
				onCancel={() => {
					setEditModalOpen(false);
					setEditingReward(null);
					editForm.resetFields();
				}}
				onOk={() => editForm.submit()}
				centered
				destroyOnClose
			>
				<Form form={editForm} layout="vertical" onFinish={handleEditReward}>
					<Form.Item
						name="name"
						label="Reward Name"
						rules={[{ required: true, message: 'Please enter reward name' }]}
					>
						<Input placeholder="Enter reward name" />
					</Form.Item>
					<Form.Item name="description" label="Description">
						<Input.TextArea rows={3} placeholder="Enter description" />
					</Form.Item>
					<Form.Item name="image" label="Image URL">
						<Input placeholder="Enter image URL" />
					</Form.Item>
					<Form.Item name="active" label="Status" valuePropName="checked">
						<Switch checkedChildren="Active" unCheckedChildren="Inactive" />
					</Form.Item>
				</Form>
			</Modal>
		</div>
	);
};

export default MilestoneRewardCard;
